import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface DrawerPushProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position?: 'left' | 'right';
  width?: string;
  children: React.ReactNode;
  title?: string;
  footer?: React.ReactNode;
  className?: string;
  drawerClassName?: string;
  mainContent?: React.ReactNode;
}

/**
 * DrawerPush component that pushes page content instead of overlaying it
 * Used for driver selection and other side panels where content should remain visible
 */
export const DrawerPush: React.FC<DrawerPushProps> = ({
  open,
  onOpenChange,
  position = 'right',
  width = '400px',
  children,
  title,
  footer,
  className = '',
  drawerClassName = '',
  mainContent
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open, onOpenChange]);

  const isLeft = position === 'left';

  // If mainContent is provided, render the flex layout
  if (mainContent) {
    return (
      <div className={`flex h-full relative ${className}`}>
        {/* Main Content Area */}
        <div
          className="flex-1 transition-all duration-300 ease-in-out overflow-auto"
          style={{
            marginRight: !isLeft && open ? '0' : '0',
            marginLeft: isLeft && open ? '0' : '0',
          }}
        >
          {mainContent}
        </div>

        {/* Drawer Panel */}
        <div
          ref={drawerRef}
          className={`
            ${open ? 'w-auto' : 'w-0'}
            ${isLeft ? 'order-first' : 'order-last'}
            bg-white dark:bg-gray-800 border-l dark:border-gray-700
            transition-all duration-300 ease-in-out
            overflow-hidden flex flex-col shadow-xl
            z-[60]
            ${drawerClassName}
          `}
          style={{
            width: open ? width : '0',
          }}
        >
          <div className="flex flex-col h-full" style={{ width }}>
            {/* Header */}
            {title && (
              <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 bg-panel">
                <h2 className="text-lg font-semibold text-heading">
                  {title}
                </h2>
                <button
                  onClick={() => onOpenChange(false)}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close drawer"
                >
                  <X className="h-5 w-5 text-muted" />
                </button>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="border-t dark:border-gray-700 bg-panel px-6 py-4">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Legacy mode: just render the drawer without main content wrapper
  return (
    <>
      {/* Drawer panel with absolute positioning within container */}
      <div
        ref={drawerRef}
        className={`
          absolute top-0 ${isLeft ? 'left-0' : 'right-0'} h-full bg-white dark:bg-gray-800
          shadow-2xl border-l dark:border-gray-700 z-[60]
          transform transition-transform duration-300 ease-in-out
          flex flex-col
          ${drawerClassName}
        `}
        style={{
          width,
          transform: open
            ? 'translateX(0)'
            : isLeft
              ? `translateX(-100%)`
              : `translateX(100%)`
        }}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 bg-panel">
            <h2 className="text-lg font-semibold text-heading">
              {title}
            </h2>
            <button
              onClick={() => onOpenChange(false)}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-muted" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t dark:border-gray-700 bg-panel px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </>
  );
};

export default DrawerPush;